import React, {
    useState
} from 'react';
import {
    Button
} from 'shared/components/ui/button';
import {
    Input
} from 'shared/components/ui/input';
import {
    Label
} from 'shared/components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    Switch
} from 'shared/components/ui/switch';
import {
    Copy, Check, ExternalLink
} from 'lucide-react';
import LongTextField from '../../primitives/LongTextField';

/**
 * IdentitySection — Identity & Voice fields per UI-MAPPING.md §2 Entity 5
 * row 1. Fields: title, description, persona, version, status, disabled,
 * assistant_token (with Regenerate trigger).
 */
export default function IdentitySection({
    a, onPatch, onRegenerateToken
}) {
    const [copied, setCopied] = useState(false);
    const [regenFlash, setRegenFlash] = useState(false);
    const [urlCopied, setUrlCopied] = useState(false);

    function copyToken() {
        const tok = a.assistant_token || '';
        if (!tok) return;
        navigator.clipboard?.writeText(tok)
            .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); })
            .catch(() => {});
    }

    // Broker config URL — derivable from assistant_id (A-0014: provisioning is
    // keyed off assistant_id, so the same URL form works whether or not the
    // tenant has been provisioned yet — auth gate handles the unprovisioned case).
    const brokerConfigUrl = a.assistant_id
        ? `${process.env.REACT_APP_CONFIG_APP_URL || window.location.origin}/provisioned-config?instance=${encodeURIComponent(a.assistant_id)}`
        : '';

    function copyBrokerUrl() {
        if (!brokerConfigUrl) return;
        navigator.clipboard?.writeText(brokerConfigUrl)
            .then(() => { setUrlCopied(true); setTimeout(() => setUrlCopied(false), 1500); })
            .catch(() => {});
    }
    function openBrokerUrl() {
        if (!brokerConfigUrl) return;
        // Sync the provisioned-config cookie to THIS assistant's token so the new
        // tab (same origin) skips the auth gate. Overwrites any stale cookie
        // from a prior assistant. SameSite=Lax + 7-day max-age — matches the
        // ProvisionedConfigAuth flow exactly.
        //
        // A stored token is never returned to the browser, so the cookie can only
        // be pre-filled right after the author typed or regenerated one. Without
        // it the new tab simply shows its own auth gate — which is correct, not
        // broken.
        const token = a.assistant_token || '';
        if (token) {
            document.cookie = `provisioned-config-key=${token}; path=/; SameSite=Lax; max-age=${7 * 24 * 60 * 60}`;
        }
        window.open(brokerConfigUrl, '_blank', 'noopener,noreferrer');
    }
    return (
        <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                    <Label>Assistant Name</Label>
                    <Input value={a.title || ''} onChange={(e) => onPatch({
                        title: e.target.value
                    })} />
                    <div className="mt-0.5 text-[10.5px] text-muted-foreground">Binds to <code>title</code> on the wire.</div>
                </div>
                <div>
                    <Label>Description</Label>
                    <LongTextField
                        label="Description"
                        value={a.description || ''}
                        onChange={(v) => onPatch({
                            description: v
                        })}
                        placeholder="What this assistant does for users."
                        previewLines={2}
                        monospace={false}
                    />
                </div>
            </div>
            <div>
                <Label>Persona</Label>
                <LongTextField
                    label="Persona"
                    value={a.persona || ''}
                    onChange={(v) => onPatch({
                        persona: v
                    })}
                    placeholder="e.g. Friendly, professional, concise."
                    helpText="This employee’s identity (system prompt, persona, guidelines, content policy) is added to the start of every instruction prompt automatically when it runs — no token in the prompt is needed."
                />
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div>
                    <Label>Version</Label>
                    <Input value={a.version || ''} onChange={(e) => onPatch({
                        version: e.target.value
                    })} />
                </div>
                <div>
                    <Label>Status</Label>
                    <Select value={a.status || 'active'} onValueChange={(v) => onPatch({
                        status: v
                    })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="active">active</SelectItem>
                            <SelectItem value="inactive">inactive</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex items-end gap-3">
                    <div>
                        <Label className="block">Disabled</Label>
                        <Switch
                            checked={!!a.disabled}
                            onCheckedChange={(v) => onPatch({
                                disabled: v
                            })}
                        />
                    </div>
                </div>
            </div>
            <div>
                <Label>Assistant Token (A2A)</Label>
                <div className="flex flex-wrap gap-2">
                    <div className="flex flex-1 min-w-0 gap-2">
                        {/* Plain text, no reveal toggle: the server redacts the
                            token on every read (redactAssistantToken), so this
                            field is empty in every state but one — the moment
                            after Regenerate or typing, when the value is the
                            caller's only copy and needs to be readable. */}
                        <Input
                            type="text"
                            value={a.assistant_token || ''}
                            placeholder={a.has_token
                                ? 'A token is set — type or regenerate to replace it'
                                : 'No token set — required for A2A'}
                            onChange={(e) => onPatch({
                                assistant_token: e.target.value
                            })}
                            className="font-mono min-w-0"
                        />
                    </div>
                    <div className="flex gap-2 shrink-0">
                        <Button
                            variant="outline"
                            type="button"
                            onClick={copyToken}
                            disabled={!a.assistant_token}
                            title="Copy to clipboard"
                            size="sm"
                        >
                            {copied
                                ? <><Check className="mr-1 h-4 w-4 text-green-600" /><span>Copied</span></>
                                : <><Copy className="mr-1 h-4 w-4" /><span>Copy</span></>}
                        </Button>
                        <Button variant="outline" size="sm" type="button" onClick={() => { onRegenerateToken(); setRegenFlash(true); setTimeout(() => setRegenFlash(false), 2000); }}>
                            Regenerate
                        </Button>
                    </div>
                </div>
                {regenFlash && (
                    <div className="mt-1 text-[10.5px] font-semibold text-green-600">✓ Token regenerated — remember to Save.</div>
                )}
                {!regenFlash && (
                    <div className="mt-0.5 text-[10.5px] text-muted-foreground">
                        {a.has_token
                            ? 'A token is set. It is never shown again — the server keeps it and injects '
                                + 'it when you run the DE from here. To hand one to an external caller, '
                                + 'type or regenerate a token and Save; that value is your only copy.'
                            : 'No token set. Required for A2A callers — regenerate one and Save.'}
                        {' '}Regenerating breaks existing callers.
                    </div>
                )}
            </div>

            {/* Provisioned tenant config URL — read-only, derived from assistant_id. */}
            <div>
                <Label>Provisioned config</Label>
                <div className="flex flex-wrap gap-2">
                    <div className="flex flex-1 min-w-0 gap-2">
                        <Input
                            type="text"
                            value={brokerConfigUrl}
                            readOnly
                            className="font-mono min-w-0"
                            onFocus={(e) => e.target.select()}
                        />
                    </div>
                    <div className="flex gap-2 shrink-0">
                        <Button
                            variant="outline"
                            type="button"
                            onClick={copyBrokerUrl}
                            disabled={!brokerConfigUrl}
                            title="Copy URL"
                            size="sm"
                        >
                            {urlCopied
                                ? <><Check className="mr-1 h-4 w-4 text-green-600" /><span>Copied</span></>
                                : <><Copy className="mr-1 h-4 w-4" /><span>Copy</span></>}
                        </Button>
                        <Button
                            variant="outline"
                            type="button"
                            onClick={openBrokerUrl}
                            disabled={!brokerConfigUrl}
                            title="Open in new tab"
                            size="sm"
                        >
                            <ExternalLink className="mr-1 h-4 w-4" />
                            <span>Open</span>
                        </Button>
                    </div>
                </div>
                <div className="mt-0.5 text-[10.5px] text-muted-foreground">
                    Tenant-facing configuration portal for this assistant. Auth uses the assistant token above.
                </div>
            </div>
        </div>
    );
}
